export const formatPillars = [
  {
    label: 'Plug-and-play modules',
    description: 'Every module opens with a one-page setup you skim in five minutes and use the same day. No theory, no chapters, no homework.',
  },
  {
    label: 'Copy-paste templates',
    description: 'Worksheets, matrices, and scripts already written for you. Fill in your own life details and start executing.',
  },
  {
    label: 'Step-by-step checklists',
    description: 'Printable daily, weekly, and reset checklists your brain can follow when your executive function is offline.',
  },
]

export const heroIncludes = [
  '6 done-for-you system modules',
  '5 bonus systems included at no extra cost',
  'Printable PDF, Google Docs and Notion formats',
  'Fill-in-the-blank worksheets, ready to use',
  'Instant access — yours forever',
]

export const moduleTitles = [
  'Module 01 — The Dopamine-Driven Task Engine',
  'Module 02 — The Anti-Overwhelm Meal & Grocery Planner',
  'Module 03 — Budget & Impulse Spending Control',
  'Module 04 — Burnout Recovery & Energy Audit',
  'Module 05 — Cleaning & Home Organization Micro-Routines',
  'Module 06 — Project Breakdown & De-Construct Playbook',
]

export const painPoints = [
  {
    emoji: '📋',
    title: 'Task Paralysis',
    text: 'You stare at your to-do list, feel overwhelmed, and end up scrolling for hours. Nothing gets done.',
  },
  {
    emoji: '💸',
    title: 'Impulse Spending',
    text: 'You buy things you don’t need for a dopamine hit, then feel guilty checking your bank account.',
  },
  {
    emoji: '🏠',
    title: 'Home Chaos',
    text: 'Walking home means facing clutter and confusion. You don’t know what to cook. Everything feels heavy.',
  },
  {
    emoji: '⚡',
    title: 'Energy Crashes',
    text: 'Some days you’re on fire; the next day you can’t get out of bed. You’re exhausted by your own brain.',
  },
  {
    emoji: '🧠',
    title: 'Mental Overload',
    text: 'A thousand ideas and reminders floating in your head. You can’t focus. You forget what matters.',
  },
  {
    emoji: '❌',
    title: 'Decision Fatigue',
    text: 'Every small choice drains you. You get stuck before you even start. Nothing feels automatic.',
  },
]

export const features = [
  {
    title: 'The Dopamine-Driven Task Engine',
    pain: 'Fixes: Task paralysis',
    description: 'Break paralysis with micro-steps. Execute tasks in 48 hours using a daily matrix that works with your brain — not against it.',
  },
  {
    title: 'The Anti-Overwhelm Meal & Grocery Planner',
    pain: 'Fixes: Home chaos',
    description: 'Low-spoon cooking. 20 meals in 15 minutes. A grocery strategy that prevents impulse buys and decision fatigue at the store.',
  },
  {
    title: 'Budget & Impulse Spending Control',
    pain: 'Fixes: Impulse spending',
    description: 'Visual money system. A 3-question filter to stop before you buy. Guilt-free spending and actual financial control.',
  },
  {
    title: 'Burnout Recovery & Energy Audit',
    pain: 'Fixes: Energy crashes',
    description: 'Spoon theory tracker. Identify your energy vampires. Reset before the crash. A 72-hour protocol to get back online.',
  },
  {
    title: 'Cleaning & Home Organization Micro-Routines',
    pain: 'Fixes: Clutter overwhelm',
    description: '5-minute sprints. “Good enough” standards. A Doom Box for chaos. Your home stops feeling like a weight.',
  },
  {
    title: 'Project Breakdown & De-Construct Playbook',
    pain: 'Fixes: Mental overload',
    description: 'Turn complex projects into simple, executable steps. No more overwhelm. Just clarity and forward motion.',
  },
]

export const included = [
  {
    title: '6 Done-for-You System Modules',
    description: 'Each is a plug-and-play toolkit with worksheets, templates, and step-by-step guides. Copy, paste, execute.',
  },
  {
    title: 'Bonus 01: The Infinite Brain-Dump Vault',
    description: 'Empty your mind onto the page. Your external hard drive for ideas, reminders, and chaos.',
  },
  {
    title: 'Bonus 02: The Emergency “Red-Level Overwhelm” Reset',
    description: 'When everything breaks down. A 24-hour protocol to get back to baseline.',
  },
  {
    title: 'Bonus 03: The Habit-Stacking Deck',
    description: 'Chain small wins. Build systems that stick without willpower.',
  },
  {
    title: 'Bonus 04: The Anti-People Pleasing Script Vault',
    description: 'Say no. Set boundaries. Stop burning out for others.',
  },
  {
    title: 'Bonus 05: The Wall-Progress Trackers',
    description: 'Visual proof of progress. Track wins, not perfection.',
  },
  {
    title: 'All Printable & Editable',
    description: 'PDF, Google Docs, and Notion formats. Use what works for your brain.',
  },
]

export const bonuses = [
  {
    emoji: '🧠',
    tag: 'Bonus #1',
    title: 'The Infinite Brain-Dump Vault',
    description: 'Empty your mind onto the page. Your external hard drive for ideas, reminders, and chaos — so nothing important lives in your head anymore.',
  },
  {
    emoji: '🚨',
    tag: 'Bonus #2',
    title: 'The Emergency “Red-Level Overwhelm” Reset',
    description: 'For the days when everything breaks down at once. A 24-hour protocol that walks you back to baseline, step by step.',
  },
  {
    emoji: '🔗',
    tag: 'Bonus #3',
    title: 'The Habit-Stacking Deck',
    description: 'Chain small wins onto things you already do. Build systems that stick without relying on willpower or motivation.',
  },
  {
    emoji: '🛡️',
    tag: 'Bonus #4',
    title: 'The Anti-People Pleasing Script Vault',
    description: 'Word-for-word scripts to say no, set boundaries, and stop burning out your energy on everyone else’s priorities.',
  },
  {
    emoji: '📊',
    tag: 'Bonus #5',
    title: 'The Wall-Progress Trackers',
    description: 'Visual proof of progress you can hang on your wall. Track wins, not perfection — the dopamine feedback your brain needs.',
  },
]

export const stats = [
  { value: '50+', label: 'Hours saved in the first month' },
  { value: '78%', label: 'Reduction in decision fatigue' },
  { value: '92%', label: 'Report better financial control' },
  { value: '89%', label: 'Feel less overwhelmed daily' },
]

export const offerChecklist = [
  'The ADHD Brain-Dump & Reset System™ — all 6 modules',
  'Bonus #1 — The Infinite Brain-Dump Vault',
  'Bonus #2 — The Emergency “Red-Level Overwhelm” Reset',
  'Bonus #3 — The Habit-Stacking Deck',
  'Bonus #4 — The Anti-People Pleasing Script Vault',
  'Bonus #5 — The Wall-Progress Trackers',
  'Printable & editable — PDF, Google Docs and Notion',
  'Instant digital access — yours forever',
]

export const outcomes = [
  'Start tasks in minutes instead of staring at the list for hours.',
  'Decide what to eat without the daily grocery-store spiral.',
  'Catch impulse purchases before they happen, with zero guilt.',
  'Spot an energy crash days before it flattens you.',
  'Come home to a space that feels lighter, not heavier.',
  'Break any big project into steps small enough to actually do.',
]

export const faqs = [
  {
    question: 'Do I need an official ADHD diagnosis to use this?',
    answer: 'No. The system was built for brains that struggle with executive function — starting tasks, remembering things, regulating energy and spending. If that describes you, diagnosed or not, everything inside applies.',
  },
  {
    question: 'I have bought planners before and never used them. Why is this different?',
    answer: 'Planners assume you already have the motivation and structure. This is a system: micro-steps, filters, and reset protocols designed for the days you have no motivation at all. There is nothing to maintain daily to keep it working.',
  },
  {
    question: 'How do I receive it after purchase?',
    answer: 'Immediately. Once your payment is complete you get instant digital access to all 6 modules and 5 bonuses, in PDF, Google Docs and Notion formats. Use them on your phone, tablet, or computer.',
  },
  {
    question: 'How long does it take to set up?',
    answer: 'Most people set up the core pieces — the brain-dump vault and the task engine — in a single sitting. Everything is fill-in-the-blank, not a course to study.',
  },
  {
    question: 'What if it does not work for my brain?',
    answer: 'You are protected by a full 7-day money-back guarantee. If the system does not help you regain clarity and control, you get a full refund. No questions asked.',
  },
]
